import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import '../styles/QRGenerator.css';

const STR = {
  th: {
    menuTitle: 'เมนูหลัก',
    home: 'หน้าหลัก',
    createQr: 'สร้าง QR Code',
    about: 'เกี่ยวกับ',
    contact: 'ติดต่อเรา',
    stats: 'สถิติ',
    bgSection: 'พื้นหลัง',
    theme: 'ธีมสี',
    bgUrl: 'รูปพื้นหลัง (URL)',
    useImage: 'ใช้รูป',
    removeImage: 'ลบรูป',
    title: 'สร้าง QR Code จาก URL',
    subtitle: 'ใส่ URL ของคุณแล้วกดสร้าง',
    urlPlaceholder: 'https://example.com',
    createBtn: 'สร้าง QR Code',
    dots: 'รูปแบบจุด (dots)',
    corners: 'มุม (corners)',
    mainColor: 'สีหลัก',
    bgColor: 'สีพื้นหลัง',
    logoUrl: 'โลโก้ (URL)',
    logoPlaceholder: 'https://.../logo.png',
    size: 'ขนาด',
    ecc: 'ระดับซ่อม (ECC)',
    adjustStyle: 'ปรับสไตล์',
    downloadPng: 'ดาวน์โหลด PNG',
    downloadSvg: 'ดาวน์โหลด SVG',
    aboutTitle: 'เกี่ยวกับ',
    aboutText:
      'เครื่องมือสร้าง QR Code ฟรี ใช้งานในเบราว์เซอร์ ไม่เก็บข้อมูล URL ของคุณบนเซิร์ฟเวอร์',
    contactTitle: 'ติดต่อเรา',
    contactText: 'สำหรับคำถามเกี่ยวกับระบบ IoT โปรดติดต่อผู้ดูแลเว็บไซต์',
    statsTitle: 'สถิติ',
    statsText: 'หน้านี้ทำงานแบบ client-side — ไม่มีการเก็บสถิติการใช้งานส่วนบุคคล',
    themes: {
      'purple-blue': 'ม่วง-ฟ้า (เริ่มต้น)',
      ocean: 'มหาสมุทร',
      sunset: 'พระอาทิตย์ตก',
      dark: 'มืด'
    }
  },
  en: {
    menuTitle: 'Main menu',
    home: 'Home',
    createQr: 'Create QR Code',
    about: 'About',
    contact: 'Contact us',
    stats: 'Statistics',
    bgSection: 'Background',
    theme: 'Color theme',
    bgUrl: 'Background image (URL)',
    useImage: 'Use image',
    removeImage: 'Remove image',
    title: 'Create QR Code from URL',
    subtitle: 'Enter your URL and click create',
    urlPlaceholder: 'https://example.com',
    createBtn: 'Create QR Code',
    dots: 'Dot style',
    corners: 'Corners',
    mainColor: 'Main color',
    bgColor: 'Background color',
    logoUrl: 'Logo (URL)',
    logoPlaceholder: 'https://.../logo.png',
    size: 'Size',
    ecc: 'Error correction (ECC)',
    adjustStyle: 'Adjust style',
    downloadPng: 'Download PNG',
    downloadSvg: 'Download SVG',
    aboutTitle: 'About',
    aboutText:
      'Free QR generator running in your browser. Your URL is not stored on our server.',
    contactTitle: 'Contact',
    contactText: 'For IoT system questions, please contact the site administrator.',
    statsTitle: 'Statistics',
    statsText: 'This page runs client-side — no personal usage statistics are collected.',
    themes: {
      'purple-blue': 'Purple-Blue (default)',
      ocean: 'Ocean',
      sunset: 'Sunset',
      dark: 'Dark'
    }
  }
};

const DOT_TYPES = [
  { value: 'square', labelEn: 'Square', labelTh: 'สี่เหลี่ยม' },
  { value: 'dots', labelEn: 'Dots', labelTh: 'จุด' },
  { value: 'rounded', labelEn: 'Rounded', labelTh: 'มน' },
  { value: 'extra-rounded', labelEn: 'Extra rounded', labelTh: 'มนมาก' },
  { value: 'classy', labelEn: 'Classy', labelTh: 'คลาสสิก' },
  { value: 'classy-rounded', labelEn: 'Classy rounded', labelTh: 'คลาสสิกมน' }
];

const THEME_KEYS = ['purple-blue', 'ocean', 'sunset', 'dark'];

const CORNER_TYPES = [
  { value: 'square', labelEn: 'Square', labelTh: 'สี่เหลี่ยม', square: 'square', dot: 'square' },
  { value: 'extra-rounded', labelEn: 'Extra rounded', labelTh: 'มนมาก', square: 'extra-rounded', dot: 'dot' },
  { value: 'dot', labelEn: 'Dot', labelTh: 'จุด', square: 'square', dot: 'dot' }
];

function mapCorner(cornerValue) {
  const c = CORNER_TYPES.find((x) => x.value === cornerValue) || CORNER_TYPES[0];
  return { square: c.square, dot: c.dot };
}

function QRGenerator() {
  const [lang, setLang] = useState('th');
  const [panel, setPanel] = useState('qr');
  const [url, setUrl] = useState('https://example.com');
  const [theme, setTheme] = useState('purple-blue');
  const [bgImageInput, setBgImageInput] = useState('');
  const [appliedBgImage, setAppliedBgImage] = useState('');
  const [dotType, setDotType] = useState('square');
  const [cornerType, setCornerType] = useState('square');
  const [mainColor, setMainColor] = useState('#667eea');
  const [qrBgColor, setQrBgColor] = useState('#ffffff');
  const [logoUrl, setLogoUrl] = useState('');
  const [size, setSize] = useState(280);
  const [ecc, setEcc] = useState('M');

  const containerRef = useRef(null);
  const qrRef = useRef(null);
  const t = STR[lang];

  const buildOptions = useCallback(() => {
    const corners = mapCorner(cornerType);
    const data = (url || '').trim() || 'https://example.com';
    const opts = {
      width: size,
      height: size,
      type: 'canvas',
      data,
      margin: 8,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: ecc
      },
      dotsOptions: {
        color: mainColor,
        type: dotType
      },
      backgroundOptions: {
        color: qrBgColor
      },
      cornersSquareOptions: {
        color: mainColor,
        type: corners.square
      },
      cornersDotOptions: {
        color: mainColor,
        type: corners.dot
      }
    };
    if (logoUrl && logoUrl.trim().startsWith('http')) {
      opts.image = logoUrl.trim();
      opts.imageOptions = {
        hideBackgroundDots: true,
        imageSize: 0.35,
        margin: 8,
        crossOrigin: 'anonymous'
      };
    }
    return opts;
  }, [url, dotType, cornerType, mainColor, qrBgColor, logoUrl, size, ecc]);

  useEffect(() => {
    if (panel !== 'qr') {
      qrRef.current = null;
      return;
    }
    const el = containerRef.current;
    if (!el) return;

    const opts = buildOptions();
    if (!qrRef.current) {
      el.innerHTML = '';
      qrRef.current = new QRCodeStyling(opts);
      qrRef.current.append(el);
    } else {
      qrRef.current.update(opts);
    }
  }, [panel, buildOptions]);

  const handleCreate = () => {
    if (containerRef.current && qrRef.current) {
      qrRef.current.update(buildOptions());
    }
  };

  const applyBgImage = () => {
    setAppliedBgImage(bgImageInput.trim());
  };

  const clearBgImage = () => {
    setBgImageInput('');
    setAppliedBgImage('');
  };

  const downloadPng = () => {
    qrRef.current?.download({ name: 'qrcode', extension: 'png' });
  };

  const downloadSvg = () => {
    qrRef.current?.download({ name: 'qrcode', extension: 'svg' });
  };

  const bgClass = `qr-page-bg theme-${theme}${appliedBgImage ? ' has-image' : ''}`;
  const bgStyle = appliedBgImage
    ? { backgroundImage: `url(${appliedBgImage})` }
    : {};

  return (
    <div className="qr-page">
      <div className={bgClass} style={bgStyle} aria-hidden />

      <aside className="qr-sidebar">
        <h2>{t.menuTitle}</h2>
        <div className="qr-lang-btns">
          <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>
            EN
          </button>
          <button type="button" className={lang === 'th' ? 'active' : ''} onClick={() => setLang('th')}>
            ไทย
          </button>
        </div>

        <ul className="qr-nav">
          <li>
            <Link to="/" className="qr-nav-link">
              🏠 {t.home}
            </Link>
          </li>
          <li>
            <button
              type="button"
              className={panel === 'qr' ? 'active' : ''}
              onClick={() => setPanel('qr')}
            >
              📄 {t.createQr}
            </button>
          </li>
          <li>
            <button
              type="button"
              className={panel === 'about' ? 'active' : ''}
              onClick={() => setPanel('about')}
            >
              ℹ️ {t.about}
            </button>
          </li>
          <li>
            <button
              type="button"
              className={panel === 'contact' ? 'active' : ''}
              onClick={() => setPanel('contact')}
            >
              📞 {t.contact}
            </button>
          </li>
          <li>
            <button
              type="button"
              className={panel === 'stats' ? 'active' : ''}
              onClick={() => setPanel('stats')}
            >
              📊 {t.stats}
            </button>
          </li>
        </ul>

        <div className="qr-sidebar-section">
          <h3>{t.bgSection}</h3>
          <label htmlFor="qr-theme">{t.theme}</label>
          <select id="qr-theme" value={theme} onChange={(e) => setTheme(e.target.value)}>
            {THEME_KEYS.map((key) => (
              <option key={key} value={key}>
                {t.themes[key]}
              </option>
            ))}
          </select>
          <label htmlFor="qr-bg-url">{t.bgUrl}</label>
          <input
            id="qr-bg-url"
            type="text"
            value={bgImageInput}
            onChange={(e) => setBgImageInput(e.target.value)}
            placeholder="https://..."
          />
          <div className="qr-bg-actions">
            <button type="button" className="btn-use" onClick={applyBgImage}>
              {t.useImage}
            </button>
            <button type="button" className="btn-clear" onClick={clearBgImage}>
              {t.removeImage}
            </button>
          </div>
        </div>
      </aside>

      <main className="qr-main">
        {panel !== 'qr' && (
          <div className="qr-card qr-static-page">
            {panel === 'about' && (
              <>
                <h2>{t.aboutTitle}</h2>
                <p>{t.aboutText}</p>
              </>
            )}
            {panel === 'contact' && (
              <>
                <h2>{t.contactTitle}</h2>
                <p>{t.contactText}</p>
              </>
            )}
            {panel === 'stats' && (
              <>
                <h2>{t.statsTitle}</h2>
                <p>{t.statsText}</p>
              </>
            )}
          </div>
        )}

        {panel === 'qr' && (
          <div className="qr-card">
            <h1>{t.title}</h1>
            <p className="subtitle">{t.subtitle}</p>

            <div className="qr-url-row">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t.urlPlaceholder}
              />
              <button type="button" className="qr-btn-primary" onClick={handleCreate}>
                {t.createBtn}
              </button>
            </div>

            <div className="qr-panel">
              <div className="qr-panel-grid">
                <div className="qr-field">
                  <label>{t.dots}</label>
                  <select value={dotType} onChange={(e) => setDotType(e.target.value)}>
                    {DOT_TYPES.map((d) => (
                      <option key={d.value} value={d.value}>
                        {lang === 'th' ? d.labelTh : d.labelEn}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="qr-field">
                  <label>{t.corners}</label>
                  <select value={cornerType} onChange={(e) => setCornerType(e.target.value)}>
                    {CORNER_TYPES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {lang === 'th' ? c.labelTh : c.labelEn}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="qr-field">
                  <label>{t.mainColor}</label>
                  <input type="color" value={mainColor} onChange={(e) => setMainColor(e.target.value)} />
                </div>
                <div className="qr-field">
                  <label>{t.bgColor}</label>
                  <input type="color" value={qrBgColor} onChange={(e) => setQrBgColor(e.target.value)} />
                </div>
                <div className="qr-field" style={{ gridColumn: '1 / -1' }}>
                  <label>{t.logoUrl}</label>
                  <input
                    type="url"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder={t.logoPlaceholder}
                  />
                </div>
                <div className="qr-field" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    {t.size}: {size}px
                  </label>
                  <input
                    type="range"
                    min={120}
                    max={512}
                    step={8}
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                  />
                </div>
                <div className="qr-field">
                  <label>{t.ecc}</label>
                  <select value={ecc} onChange={(e) => setEcc(e.target.value)}>
                    <option value="L">L</option>
                    <option value="M">M</option>
                    <option value="Q">Q</option>
                    <option value="H">H</option>
                  </select>
                </div>
              </div>
              <div className="qr-panel-footer">
                <button type="button" className="qr-btn-secondary" onClick={handleCreate}>
                  {t.adjustStyle}
                </button>
              </div>
            </div>

            <div className="qr-preview-wrap">
              <div id="qr-canvas-container" ref={containerRef} />
              <div className="qr-download-row">
                <button type="button" onClick={downloadPng}>
                  {t.downloadPng}
                </button>
                <button type="button" onClick={downloadSvg}>
                  {t.downloadSvg}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default QRGenerator;
